import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  SettingRow,
} from "@playerelite/ui";
import type { IconName, SettingRowProps } from "@playerelite/ui";

/** Global kiosk display timings, in seconds. */
export interface KioskSettings {
  timeToChangeAttractor: number;
  timeUntilBackgroundMode: number;
  timeToRefreshBackendData: number;
}

const SETTING_SPECS: {
  key: keyof KioskSettings;
  icon: IconName;
  tone: SettingRowProps["tone"];
  title: string;
  description: string;
}[] = [
  {
    key: "timeToChangeAttractor",
    icon: "bolt",
    tone: "warning",
    title: "Time to Change Attractor",
    description:
      "How long each attractor is displayed before cycling to the next",
  },
  {
    key: "timeUntilBackgroundMode",
    icon: "toggle",
    tone: "primary",
    title: "Time Until Background Mode",
    description:
      "Idle duration before the kiosk enters background / screensaver mode",
  },
  {
    key: "timeToRefreshBackendData",
    icon: "refresh",
    tone: "success",
    title: "Time to Refresh Backend Data",
    description: "Interval between backend data sync cycles",
  },
];

/** The minimum any of these timings may be set to. Below this the kiosk thrashes. */
const MIN_SECONDS = 5;

/** Held as strings so a field can be cleared and retyped. */
type SettingsDraft = Record<keyof KioskSettings, string>;

const draftFrom = (settings: KioskSettings): SettingsDraft => ({
  timeToChangeAttractor: String(settings.timeToChangeAttractor),
  timeUntilBackgroundMode: String(settings.timeUntilBackgroundMode),
  timeToRefreshBackendData: String(settings.timeToRefreshBackendData),
});

function secondsError(raw: string): string | undefined {
  const value = Number(raw);
  if (raw.trim() === "" || !Number.isFinite(value)) {
    return "Enter a number of seconds";
  }
  if (!Number.isInteger(value)) return "Whole seconds only";
  if (value < MIN_SECONDS) return `Must be at least ${MIN_SECONDS} seconds`;
  return undefined;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  settings: KioskSettings;
  /** Rejecting keeps the dialog open and shows the error's message. */
  onSubmit: (settings: KioskSettings) => Promise<void>;
}

/** The Kiosk Configuration modal from the Storybook design: one SettingRow per timing. */
const KioskSettingsDialog = ({
  open,
  onOpenChange,
  settings,
  onSubmit,
}: Props) => {
  const [draft, setDraft] = useState<SettingsDraft>(() => draftFrom(settings));
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string>();

  // Re-seed whenever the dialog opens, so a cancelled edit is genuinely discarded.
  useEffect(() => {
    if (open) {
      setDraft(draftFrom(settings));
      setSubmitted(false);
      setSubmitError(undefined);
    }
  }, [open, settings]);

  const errors = SETTING_SPECS.map((spec) => secondsError(draft[spec.key]));

  const save = async () => {
    setSubmitted(true);
    if (errors.some(Boolean)) return;
    setSaving(true);
    setSubmitError(undefined);
    try {
      await onSubmit({
        timeToChangeAttractor: Number(draft.timeToChangeAttractor),
        timeUntilBackgroundMode: Number(draft.timeUntilBackgroundMode),
        timeToRefreshBackendData: Number(draft.timeToRefreshBackendData),
      });
      onOpenChange(false);
    } catch (error) {
      setSubmitError(
        error instanceof Error && error.message
          ? error.message
          : "Something went wrong. Try again."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (saving ? undefined : onOpenChange(next))}
    >
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Kiosk Configuration</DialogTitle>
          <DialogDescription>
            Global display settings for every kiosk at this venue
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="gap-3">
          {submitError ? (
            <Alert variant="danger" title="Couldn't save">
              {submitError}
            </Alert>
          ) : null}
          {SETTING_SPECS.map((spec, i) => (
            <SettingRow
              key={spec.key}
              icon={spec.icon}
              tone={spec.tone}
              title={spec.title}
              description={spec.description}
              unit="seconds"
              error={submitted ? errors[i] : undefined}
              disabled={saving}
            >
              <Input
                type="number"
                inputMode="numeric"
                min={MIN_SECONDS}
                className="w-20"
                value={draft[spec.key]}
                onChange={(e) =>
                  setDraft((prev) => ({ ...prev, [spec.key]: e.target.value }))
                }
              />
            </SettingRow>
          ))}
        </DialogBody>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary" disabled={saving}>
              Cancel
            </Button>
          </DialogClose>
          <Button variant="action" loading={saving} onClick={save}>
            Save Settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default KioskSettingsDialog;
